/* Marketing home + /video: the 90-second cartoon explainer — English on the English page, Arabic on the Arabic page.
 * Files live in web/public/media (1280×720 H.264, faststart) and only download on play.
 * The share bar links to the standalone page (/video/, /ar/video/) so the video can be sent on its own. */
import type { Lang } from "../../../data/types";
import { useI18n } from "../../../i18n";
import { Icon, useToast } from "../../../ui";
import { SITE_URL } from "../../../seo/meta";
import { onStaticPage, seoPath } from "../../../seo/paths";

/* Prerendered pages live in sub-folders (/ar/, /industries/…/), where the relative build base "./" would miss /media. */
const base = () => (onStaticPage() ? "/" : import.meta.env.BASE_URL);
const LENGTH: Record<Lang, string> = { en: "1:35", ar: "1:43" };
export const explainer = (lang: Lang) => ({ src: base() + `media/starthr-explainer-${lang}.mp4`, poster: base() + `media/starthr-explainer-${lang}.jpg`, length: LENGTH[lang] });

/** Absolute link to the standalone video page in the given language: its crawlable URL (/video/, /ar/video/),
 * so link previews show the video page's own title and poster. */
export function videoShareUrl(lang: Lang, origin: string = typeof window === "undefined" ? SITE_URL : window.location.origin) {
  return origin + seoPath("/video", lang);
}

async function copyText(text: string) {
  try { await navigator.clipboard.writeText(text); } catch {
    const ta = document.createElement("textarea"); ta.value = text; document.body.appendChild(ta); ta.select();
    try { document.execCommand("copy"); } catch { /* ignore */ } ta.remove();
  }
}

function ShareBar({ lang }: { lang: Lang }) {
  const { L } = useI18n();
  const toast = useToast();
  const url = videoShareUrl(lang);
  const title = L("StartHR in 90 seconds", "ستارت HR في 90 ثانية");
  const text = L("HR and payroll for Saudi businesses — watch the 90-second video:", "الموارد البشرية والرواتب للمنشآت السعودية — شاهد الفيديو في 90 ثانية:");
  const share = async () => {
    const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
    if (typeof nav.share === "function") {
      try { await nav.share({ title, text, url }); return; } catch (e) { if ((e as { name?: string })?.name === "AbortError") return; }
    }
    await copyText(url);
    toast(L("Video link copied", "تم نسخ رابط الفيديو"));
  };
  const copy = async () => { await copyText(url); toast(L("Video link copied", "تم نسخ رابط الفيديو")); };
  return (
    <div className="row xv-share" data-testid="explainer-share">
      <button type="button" className="btn sm pri" onClick={share} data-testid="explainer-share-btn"><Icon name="share" className="s" /><span>{L("Share video", "مشاركة الفيديو")}</span></button>
      <button type="button" className="btn sm" onClick={copy} data-testid="explainer-copy"><Icon name="link" className="s" /><span>{L("Copy link", "نسخ الرابط")}</span></button>
      <a className="btn sm" href={"https://wa.me/?text=" + encodeURIComponent(text + " " + url)} target="_blank" rel="noopener noreferrer" data-testid="explainer-wa"><Icon name="phone" className="s" /><span>{L("WhatsApp", "واتساب")}</span></a>
    </div>
  );
}

export function ExplainerVideo({ share = true }: { share?: boolean }) {
  const { L, lang } = useI18n();
  const v = explainer(lang);

  return (
    <div className="xv" data-testid="explainer">
      <div className="xv-frame">
        <video
          key={lang}
          className="xv-video"
          controls
          playsInline
          preload="none"
          poster={v.poster}
          lang={lang}
          aria-label={L("StartHR explainer video", "فيديو تعريفي بستارت HR")}
          data-testid="explainer-video"
        >
          <source src={v.src} type="video/mp4" />
          <a href={v.src}>{L("Download the video", "تنزيل الفيديو")}</a>
        </video>
      </div>
      <div className="xv-bar">
        <span className="xv-len">{L("Length", "المدة")} <b className="num" data-testid="explainer-len">{v.length}</b></span>
        {share && <ShareBar lang={lang} />}
      </div>
    </div>
  );
}
